import React, { FC, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { FormContext } from '../../../components/form/gl-form-context';
import { Listing } from '../../../types/listing/listing';
import { Col, Row } from 'react-styled-flexboxgrid';
import SectionHeading from "../../../components/section-heading/section-heading";
import { useSelector, useDispatch } from 'react-redux';
import { Config, AspectConfig } from '../../../types/config/config';
import { configSelector } from '../../../redux/selectors/system/config-selector';
import styled from 'styled-components';
import BlueCheck from '../../../assets/images/png/blue-check.png';
import GrayCheck from '../../../assets/images/png/gray-check.png';
import FormSelect from '../../../components/form-select/form-select';
import { loadMiqAmenities } from '../../../redux/actions/system/load-miq-amenities';
import { getMiqAmenitiesByKey } from '../../../redux/selectors/system/miq-amenities-selector';
import { normalizeToKeys, getDisplayValue, getAvailableOptions } from '../../../utils/amenities-mapper';
import { State } from '../../../types/state';

export interface Props {
    listing: Listing
}

const Amenities: FC<Props> = (props) => {

    const { listing } = props;
    const config: Config = useSelector(configSelector);
    const dispatch = useDispatch();
    const formControllerContext = useContext(FormContext);
    
    const hasAspectsConfig = config.aspects && config.aspects.show && config.aspects.options;
    const hasAmenitiesConfig = config.amenities && config.amenities.show === true;
    const miqCountryCode = config.miqCountryCode;
    
    if (!hasAspectsConfig && !hasAmenitiesConfig) {
        return null;
    }
    
    const useMiqApi = hasAmenitiesConfig && config.amenities.useMiqApi === true;
    
    const configuredOptions: AspectConfig[] = (config.aspects && config.aspects.options) ? config.aspects.options : [];
    
    // Extract propertyType to prevent unnecessary selector re-renders
    const propertyType = listing.propertyType;
    
    // Get amenities from Redux cache using country_propertyType as key
    const miqApiOptions = useSelector((state: State) => 
        useMiqApi && miqCountryCode && propertyType 
            ? getMiqAmenitiesByKey(state, miqCountryCode, propertyType)
            : []
    );
    
    const getInitialSelectedItems = (): string[] => {
        if (useMiqApi) {
            const amenities = Array.isArray(listing.amenities) ? listing.amenities : [];
            return normalizeToKeys(amenities, miqApiOptions);
        }
        return Array.isArray(listing.aspects) ? listing.aspects : [];
    };
    
    const initialSelectedItems = getInitialSelectedItems();
    const [selectedItems, setSelectedItems] = useState<string[]>(initialSelectedItems);

    const defaultOptions = useMemo(() => {
        return (config.amenities && Array.isArray(config.amenities.defaultOptions) 
            ? config.amenities.defaultOptions 
            : []);
    }, [config.amenities]);

    // Load MIQ amenities when property type or country changes
    useEffect(() => {
        if (useMiqApi && propertyType && miqCountryCode && miqCountryCode.trim() !== '') {
            dispatch(loadMiqAmenities(propertyType, miqCountryCode, defaultOptions));
        }
    }, [propertyType, miqCountryCode, useMiqApi, defaultOptions, dispatch]);

    useEffect(() => {
        if (useMiqApi && miqApiOptions.length > 0) {
            const amenities = Array.isArray(listing.amenities) ? listing.amenities : [];
            const normalized = normalizeToKeys(amenities, miqApiOptions);
            setSelectedItems(normalized);
        }
    }, [miqApiOptions, listing.amenities, useMiqApi]);
    
    const displayOptions: AspectConfig[] = useMemo(() => {
        if (useMiqApi && configuredOptions.length === 0) {
            return initialSelectedItems.map((amenityKey: string) => ({
                id: amenityKey,
                display: getDisplayValue(amenityKey, miqApiOptions),
                default: false
            }));
        }
        return configuredOptions;
    }, [useMiqApi, configuredOptions, initialSelectedItems, miqApiOptions]);

    const saveToListing = useCallback((updatedItems: string[]) => {
        const values: { [key: string]: string[] } = {};
        
        if (useMiqApi) {
            values.amenities = updatedItems;
        } else {
            values.aspects = updatedItems;
        }
        
        formControllerContext.onFormChange(values);
    }, [useMiqApi, formControllerContext]);

    const updateSelection = useCallback((itemId: string, shouldAdd: boolean) => {
        if (!itemId) {
            return;
        }
        
        const updatedList = shouldAdd 
            ? selectedItems.concat(itemId)
            : selectedItems.filter(id => id !== itemId);
        
        setSelectedItems(updatedList);
        saveToListing(updatedList);
    }, [selectedItems, saveToListing]);

    const toggleItem = useCallback((item: AspectConfig) => {
        const isCurrentlySelected = selectedItems.includes(item.id);
        updateSelection(item.id, !isCurrentlySelected);
    }, [selectedItems, updateSelection]);

    const removeItem = useCallback((itemId: string) => {
        updateSelection(itemId, false);
    }, [updateSelection]);

    const handleMiqDropdownSelection = useCallback((selectedKey: string) => {
        if (!selectedKey || selectedItems.includes(selectedKey)) {
            return;
        }
        updateSelection(selectedKey, true);
    }, [selectedItems, updateSelection]);

    // Memoize available options to prevent recalculation on every render
    const availableOptions = useMemo(() => {
        return getAvailableOptions(miqApiOptions, selectedItems);
    }, [miqApiOptions, selectedItems]);

    const renderMiqDropdown = useCallback(() => (
        <MiqDropdownCol xs={6}>
            <FormSelect
                id="miq-amenities-dropdown"
                name="miqAmenities"
                label="Add Amenity"
                options={availableOptions}
                prompt="Select an amenity..."
                changeHandler={handleMiqDropdownSelection}
                isClearable={true}
                
            />
        </MiqDropdownCol>
    ), [availableOptions, handleMiqDropdownSelection]);

    const renderMiqChips = useCallback(() => {
        if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
            return null;
        }
        
        return (
            <>
                {selectedItems.map((itemKey: string) => {
                    if (!itemKey) {
                        return null;
                    }
                    
                    return (
                        <div key={itemKey}>
                            <AmenityThumbnail
                                data-testid="amenity"
                                style={selectedAmenityStyle}
                                onClick={() => removeItem(itemKey)}
                            >
                                <AmenityIcon style={selectedIconStyle}>
                                    <img src={BlueCheck} alt="Amenity selected" />
                                </AmenityIcon>
                                {getDisplayValue(itemKey, miqApiOptions)}
                            </AmenityThumbnail>
                        </div>
                    );
                })}
            </>
        );
    }, [selectedItems, miqApiOptions, removeItem]);

    const renderConfiguredOptions = useCallback(() => {
        if (!Array.isArray(displayOptions) || displayOptions.length === 0) {
            return null;
        }
        
        return (
            <>
                {displayOptions.map((item: AspectConfig) => {
                    if (!item || !item.id) {
                        return null;
                    }
                    
                    const isSelected = selectedItems.includes(item.id);
                    
                    return (
                        <div key={item.id}>
                            <AmenityThumbnail 
                                data-testid={'amenity'} 
                                style={isSelected ? selectedAmenityStyle : unselectedAmenityStyle} 
                                onClick={() => toggleItem(item)}
                            >
                                <AmenityIcon style={isSelected ? selectedIconStyle : unselectedIconStyle}>
                                    <img 
                                        src={isSelected ? BlueCheck : GrayCheck} 
                                        alt={`Amenity ${isSelected ? 'selected' : 'not selected'}`} 
                                    />
                                </AmenityIcon>
                                {item.display}
                            </AmenityThumbnail>
                        </div>
                    );
                })}
            </>
        );
    }, [displayOptions, selectedItems, toggleItem]);

    return (
        <AmenityContainer id="amenities-section">
            <Row id="aspects" style={{ marginTop: "25px" }}>
                <Col xs={12}>
                    <SectionHeading>Amenities</SectionHeading>
                </Col>
                
                {useMiqApi && renderMiqDropdown()}
                
                <Col xs={12}>
                    <AmenitiesContainer id="amenities-chips-container">
                        {useMiqApi ? renderMiqChips() : renderConfiguredOptions()}
                    </AmenitiesContainer>
                </Col>
            </Row>
        </AmenityContainer>
    );
}


const selectedAmenityStyle = { backgroundColor: '#E7F6FA', border: '1px #059FC4 solid' };
const unselectedAmenityStyle = { backgroundColor: '#fff', border: '1px #dedede solid', color: '#999' };
const selectedIconStyle = { backgroundColor: '#E7F6FA' };
const unselectedIconStyle = { backgroundColor: '#F6F6F6' };

const MiqDropdownCol = styled(Col)`
    margin-bottom: 20px;
`;

const AmenitiesContainer = styled.div`
    display:flex;
    flex-direction: row;
    flex-wrap: wrap;
    max-width:730px;

    > div {
        margin-right:11px;
        margin-top:15px;
    }
`;

const AmenityContainer = styled.div`

`;

const AmenityThumbnail = styled.div`
    cursor: pointer;
    color: #666666;
    border-radius: 5px;
    width: auto;
    text-align: center;
    padding:8px;
    padding-right:12px;
    display:flex;
    justify-content: center;
    align-items:center;
    border: 1px solid #dedede;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
    color: #666666;

`;

const AmenityIcon = styled.div`
    margin-right: 7px;
    margin-top:1px;
    > img {
        margin-bottom:-1px;
    }
`;


export default React.memo(Amenities, (prevProps, nextProps) => prevProps.listing === nextProps.listing);

